import { Link, Text, useSnackbars } from '@customerio/pluma-components/react';
import { useMutation } from '@tanstack/react-query';
import { useAnalytics } from '@/services/analytics';
import { hydraUiApi } from '@/services/api';
import { useAuth } from '@/services/auth';
import { download } from '@/utils/download';
import { sentryCaptureException } from '@/utils/sentry';
import { getFileIcon } from '../utils/get-file-icon';
import { getMimeTypeFromPath } from '../utils/get-mime-type';
import { isSystemPath } from '../utils/is-system-path';

type CopilotFileLinkProps = { path: string; displayName: string };

export function CopilotFileLink({ path, displayName }: CopilotFileLinkProps) {
  const auth = useAuth();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const mimeType = getMimeTypeFromPath(path);
  const isSystem = isSystemPath(path);

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await hydraUiApi.copilotFiles.get({
        params: { path: { account_id: String(auth.account.id), file_path: path } },
      });

      const blob = new Blob([response.file.content], { type: mimeType });
      const filename = path.split('/').pop() ?? path;
      await download(blob, filename);
      const extension = filename.split('.').pop()?.toLowerCase() ?? 'unknown';
      track('aiAssistant', 'fileDownloaded', { 'File Type': extension });
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to download file. Please try again.',
        variant: 'critical',
      });
    },
  });

  if (isSystem) {
    return <Text size="sm">{displayName}</Text>;
  }

  return (
    <Link
      onClick={() => downloadMutation.mutate()}
      icon={getFileIcon(mimeType)}
      isLoading={downloadMutation.isPending}
      variant="secondary"
    >
      {displayName}
    </Link>
  );
}
